/**
 * Modelos genéricos para paginación server-side.
 *
 * Reutilizables para cualquier listado paginado de la plataforma
 * (proyectos, estimaciones, snapshots, usuarios, etc.).
 *
 * @version: 19/05/2026_1
 */

/**
 * Respuesta estándar de un endpoint paginado.
 *
 * Refleja el contrato del backend:
 *   { items, total, page, pageSize, totalPages }
 *
 * @template T - Tipo del item individual del listado.
 */
export interface PagedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Parámetros base de query string para cualquier listado paginado.
 *
 * Los listados específicos extienden esta interfaz para añadir sus propios
 * filtros (societyId, status, etc.).
 *
 *  - `sort`: '<campo>:<dir>' (ej. 'name:asc', 'creationdate:desc').
 *            El backend aplica una whitelist; valores no permitidos caen al default.
 *  - `q`:    búsqueda libre en los campos relevantes del recurso (definido por el backend).
 */
export interface PageQueryParams {
  page?: number;
  pageSize?: number;
  sort?: string;
  q?: string;
}

/**
 * Direcciones de ordenación válidas.
 * Útil para tipar controles de UI que construyen el parámetro `sort`.
 */
export type SortDir = 'asc' | 'desc';

/**
 * Defaults de paginación aplicados de forma consistente en toda la plataforma.
 * El componente <app-paginator> los consume si no se le pasan props explícitos.
 */
export const DEFAULT_PAGE_SIZE_OPTIONS: readonly number[] = [10, 25, 50, 100];
export const DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE_OPTIONS[0];